import React, { useEffect, useState } from 'react';
import { Button, Drawer, Input, Modal, Segmented, Skeleton, Switch, Tooltip } from 'antd';
import {
  CameraOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExportOutlined,
  EyeOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  projectService,
  Project,
  ProjectShareLink,
  ShareLanguage,
  ShareLinkInput,
  SharePermission,
  SHARE_PERMISSIONS,
  shareLinkUrl,
} from '../../services/projectService';
import { EmptyState, Pill } from '../ui';
import './ProjectShareDrawer.css';

type Permissions = Record<SharePermission, boolean>;

const NO_PERMISSIONS: Permissions = {
  show_documents: false,
  show_plan: false,
  show_photos_before: false,
  show_photos_after: false,
  show_team_days: false,
};

const PERMISSION_ICON: Record<SharePermission, React.ReactNode> = {
  show_documents: <FileTextOutlined />,
  show_plan: <UnorderedListOutlined />,
  show_photos_before: <CameraOutlined />,
  show_photos_after: <PictureOutlined />,
  show_team_days: <TeamOutlined />,
};

const LANGUAGES: ShareLanguage[] = ['de', 'en', 'sq'];

/** Copies text; falls back to a hidden textarea where the Clipboard API isn't available. */
const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
};

/** The five opt-in sections as switches, shared by the "new link" form and each existing link. */
const PermissionList: React.FC<{
  value: Permissions;
  onChange: (key: SharePermission, checked: boolean) => void;
  disabled?: boolean;
  compact?: boolean;
}> = ({ value, onChange, disabled, compact }) => {
  const { t } = useTranslation();
  return (
    <div className={`psh-perms ${compact ? 'psh-perms--compact' : ''}`}>
      {SHARE_PERMISSIONS.map((key) => (
        <label key={key} className={`psh-perm ${value[key] ? 'is-on' : ''}`}>
          <span className="psh-perm-icon" aria-hidden>
            {PERMISSION_ICON[key]}
          </span>
          <span className="psh-perm-text">
            <span className="psh-perm-title">{t(`projects.share.permissions.${key}`)}</span>
            {!compact && <span className="psh-perm-hint">{t(`projects.share.permissionHints.${key}`)}</span>}
          </span>
          <Switch
            size={compact ? 'small' : 'default'}
            checked={value[key]}
            disabled={disabled}
            onChange={(checked) => onChange(key, checked)}
          />
        </label>
      ))}
    </div>
  );
};

/**
 * Projects → Share with client: read-only links the admin copies and sends. Every link shows the
 * project's name, location, status and dates; documents, the plan board, before / after photos and
 * who worked on which day are switched on per link (off by default) and can be changed later.
 */
const ProjectShareDrawer: React.FC<{ project: Project | null; onClose: () => void }> = ({ project, onClose }) => {
  const { t, i18n } = useTranslation();
  const [modal, modalContextHolder] = Modal.useModal();
  const [links, setLinks] = useState<ProjectShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [language, setLanguage] = useState<ShareLanguage>('de');
  const [permissions, setPermissions] = useState<Permissions>(NO_PERMISSIONS);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [freshId, setFreshId] = useState<number | null>(null);

  const projectId = project?.id;

  const resetForm = () => {
    setLabel('');
    setLanguage('de');
    setPermissions(NO_PERMISSIONS);
  };

  const load = async (id: number) => {
    setLoading(true);
    try {
      const list = await projectService.getShareLinks(id);
      setLinks(list);
      setFormOpen(list.length === 0);
    } catch {
      setLinks([]);
      toast.error(t('projects.share.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setLinks([]);
    setFreshId(null);
    resetForm();
    load(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const copyLink = async (link: ProjectShareLink) => {
    if (await copyText(shareLinkUrl(link.token))) {
      setCopiedId(link.id);
      window.setTimeout(() => setCopiedId((current) => (current === link.id ? null : current)), 2000);
      toast.success(t('projects.share.copied'));
    } else {
      toast.error(t('projects.share.copyError'));
    }
  };

  const handleCreate = async () => {
    if (!projectId) return;
    setCreating(true);
    try {
      const link = await projectService.createShareLink(projectId, {
        label: label.trim() || null,
        language,
        ...permissions,
      });
      setLinks((current) => [link, ...current]);
      setFreshId(link.id);
      setFormOpen(false);
      resetForm();
      // Ready to paste straight away
      if (await copyText(shareLinkUrl(link.token))) {
        setCopiedId(link.id);
        toast.success(t('projects.share.createdCopied'));
      } else {
        toast.success(t('projects.share.created'));
      }
    } catch {
      toast.error(t('projects.share.saveError'));
    } finally {
      setCreating(false);
    }
  };

  /** Optimistic: the switch flips at once and flips back if saving fails. */
  const updateLink = async (link: ProjectShareLink, changes: ShareLinkInput) => {
    setLinks((current) => current.map((item) => (item.id === link.id ? { ...item, ...changes } : item)));
    try {
      const saved = await projectService.updateShareLink(link.id, changes);
      setLinks((current) => current.map((item) => (item.id === saved.id ? saved : item)));
    } catch {
      setLinks((current) => current.map((item) => (item.id === link.id ? link : item)));
      toast.error(t('projects.share.saveError'));
    }
  };

  const confirmDelete = (link: ProjectShareLink) =>
    modal.confirm({
      title: t('projects.share.deleteConfirm'),
      content: t('projects.share.deleteConfirmText'),
      okText: t('projects.share.delete'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await projectService.deleteShareLink(link.id);
          setLinks((current) => current.filter((item) => item.id !== link.id));
          toast.success(t('projects.share.deleted'));
        } catch {
          toast.error(t('projects.share.deleteError'));
        }
      },
    });

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });

  const renderLink = (link: ProjectShareLink) => {
    const url = shareLinkUrl(link.token);
    const shown = SHARE_PERMISSIONS.filter((key) => link[key]).length;
    return (
      <article key={link.id} className={`psh-link ${freshId === link.id ? 'is-fresh' : ''}`}>
        <header className="psh-link-head">
          <div className="psh-link-title">
            <LinkOutlined aria-hidden />
            <span>{link.label || t('projects.share.untitled')}</span>
          </div>
          <Tooltip title={t('projects.share.delete')}>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(link)}
              aria-label={t('projects.share.delete')}
            />
          </Tooltip>
        </header>
        <div className="psh-link-meta">
          <span>{t('projects.share.createdOn', { date: formatDate(link.created_at) })}</span>
          <span>
            <EyeOutlined aria-hidden />{' '}
            {link.views_count > 0 && link.last_viewed_at
              ? t('projects.share.views', { count: link.views_count, date: formatDate(link.last_viewed_at) })
              : t('projects.share.notOpened')}
          </span>
        </div>

        <div className="psh-url">
          <Input readOnly value={url} onFocus={(event) => event.target.select()} aria-label={t('projects.share.url')} />
          <Button
            type={freshId === link.id ? 'primary' : 'default'}
            icon={copiedId === link.id ? <CheckOutlined /> : <CopyOutlined />}
            onClick={() => copyLink(link)}
          >
            {copiedId === link.id ? t('projects.share.copiedShort') : t('projects.share.copy')}
          </Button>
          <Tooltip title={t('projects.share.preview')}>
            <Button icon={<ExportOutlined />} href={url} target="_blank" aria-label={t('projects.share.preview')} />
          </Tooltip>
        </div>

        <div className="psh-link-section">
          <span>{t('projects.share.clientSees')}</span>
          <Pill tone={shown ? 'primary' : 'neutral'}>
            {shown ? t('projects.share.sectionsOn', { count: shown }) : t('projects.share.basicsOnly')}
          </Pill>
          <Segmented
            size="small"
            className="psh-link-language"
            value={link.language}
            options={LANGUAGES.map((value) => ({ value, label: value.toUpperCase() }))}
            onChange={(value) => updateLink(link, { language: value as ShareLanguage })}
          />
        </div>
        <PermissionList compact value={link} onChange={(key, checked) => updateLink(link, { [key]: checked })} />
      </article>
    );
  };

  return (
    <Drawer
      title={
        project ? (
          <div className="psh-title">
            <span className="psh-title-name">{t('projects.share.title')}</span>
            <span className="psh-title-sub">{project.name}</span>
          </div>
        ) : null
      }
      placement="right"
      onClose={onClose}
      open={!!project}
      width="min(560px, 100vw)"
      // Above the plan drawer, which can open it too
      zIndex={1010}
      extra={
        project ? (
          <Button
            type="text"
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => load(project.id)}
            disabled={loading}
            aria-label={t('dashboard.refresh')}
          />
        ) : null
      }
    >
      {modalContextHolder}
      {loading && links.length === 0 ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          {formOpen ? (
            <section className="psh-new">
              <h3 className="psh-new-title">{t('projects.share.newLink')}</h3>
              <label className="psh-field">
                <span>{t('projects.share.label')}</span>
                <Input
                  value={label}
                  maxLength={255}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder={t('projects.share.labelPlaceholder')}
                />
              </label>
              <div className="psh-field">
                <span>{t('projects.share.language')}</span>
                <Segmented
                  value={language}
                  options={LANGUAGES.map((value) => ({ value, label: t(`projects.share.languages.${value}`) }))}
                  onChange={(value) => setLanguage(value as ShareLanguage)}
                />
              </div>
              <div className="psh-always">
                <InfoCircleOutlined aria-hidden />
                <span>{t('projects.share.alwaysVisible')}</span>
              </div>
              <div className="psh-field">
                <span>{t('projects.share.alsoShow')}</span>
                <PermissionList
                  value={permissions}
                  onChange={(key, checked) => setPermissions((current) => ({ ...current, [key]: checked }))}
                />
              </div>
              <div className="psh-new-actions">
                {links.length > 0 && <Button onClick={() => setFormOpen(false)}>{t('common.cancel')}</Button>}
                <Button type="primary" icon={<LinkOutlined />} loading={creating} onClick={handleCreate}>
                  {t('projects.share.create')}
                </Button>
              </div>
            </section>
          ) : (
            <Button block type="dashed" icon={<PlusOutlined />} onClick={() => setFormOpen(true)} className="psh-add">
              {t('projects.share.newLink')}
            </Button>
          )}

          {links.length > 0 ? (
            <div className="psh-links">
              <h3 className="psh-links-title">{t('projects.share.existing', { count: links.length })}</h3>
              {links.map(renderLink)}
            </div>
          ) : (
            !formOpen && <EmptyState icon={<LinkOutlined />} text={t('projects.share.empty')} />
          )}
        </>
      )}
    </Drawer>
  );
};

export default ProjectShareDrawer;
